import { jwtDecode } from "jwt-decode";
import { getApiBaseUrl } from "../utils/apiConfig";

const BASE_URL = getApiBaseUrl();

const fetchDataFromEndpoint = async (endpoint) => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/${endpoint}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
  }
  return response.json();
};

export async function fetchRegistrationData() {
  try {
    const [nationalities, countries, genders, propertyTypes, districts, roles] =
      await Promise.all([
        fetchDataFromEndpoint("nationalities"),
        fetchDataFromEndpoint("countries"),
        fetchDataFromEndpoint("genders"),
        fetchDataFromEndpoint("property_types"),
        fetchDataFromEndpoint("districts"),
        fetchDataFromEndpoint("roles").catch(() => []),
      ]);

    return { nationalities, countries, genders, propertyTypes, districts, roles: roles || [] };
  } catch (error) {
    console.error("fetchRegistrationData error:", error);
    throw new Error(error.message || "One or more registration data sources failed to load.");
  }
}

// --- User Registration Methods (from original userService.js) ---

const registerTraveler = async (userData) => {
  const response = await fetch(`${BASE_URL}/users/add`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || "Traveler registration failed");
  }

  return response.json();
};

const registerGuide = async (userData) => {
  const response = await fetch(`${BASE_URL}/register/guide`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || "Guide registration failed");
  }

  return response.json();
};

const updateGuideFiles = async (fileData) => {
  const response = await fetch(`${BASE_URL}/guide/files`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(fileData),
  });

  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(responseData.message || responseData.error || "Guide file update failed");
  }

  return responseData;
};

const updateHotelOwnerFiles = async (fileData) => {
  const response = await fetch(`${BASE_URL}/hotel-owner/files`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(fileData),
  });

  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(responseData.message || responseData.error || "Hotel Owner file update failed");
  }

  return responseData;
};

const registerHotelOwner = async (userData) => {
  const response = await fetch(`${BASE_URL}/register/hotel-owner`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || "Hotel Owner registration failed");
  }

  return response.json();
};

const validateCityZip = async (city, zip) => {
  const response = await fetch(`${BASE_URL}/validate-city-zip`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ city, zip }),
  });

  const responseData = await response.json();

  if (!response.ok) {
    throw new Error(
      responseData.message || "City/Zip validation service failed"
    );
  }

  return responseData;
};

const validateNicDob = async (nic, dob) => {
  const response = await fetch(`${BASE_URL}/validate-nic-dob`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nic, dob }),
  });
  const data = await response.json();
  if (!response.ok)
    throw new Error(data.message || "NIC and DOB validation failed");
  return data.isValid;
};

const validateNicGender = async (nic, genderId) => {
  const response = await fetch(`${BASE_URL}/validate-nic-gender`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nic, genderId }),
  });
  const data = await response.json();
  if (!response.ok)
    throw new Error(data.message || "NIC and Gender validation failed");
  return data.isValid;
};

const validateCityDistrict = async (city, districtId) => {
  const response = await fetch(`${BASE_URL}/validate-city-district`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ city, districtId }),
  });
  const data = await response.json();
  if (!response.ok)
    throw new Error(data.message || "City and District validation failed");
  return data.isValid;
};

const fetchUserById = async (id) => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${BASE_URL}/users/${id}`, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json().catch(() => ({}));

  if (response.status === 200) {

    return data;
  } else {
    throw new Error(`Failed to load user ${id}: ${JSON.stringify(data)}`);
  }
};

const getAuthHeaders = async () => {
  // Assumption: getTokenFromStorage is an async function that reads the value for 'jwt_token'.
  const token = await getTokenFromStorage("jwt_token");

  if (!token) {
    // Dart: throw Exception('Authentication token not found. Please log in.');
    // JS: Throw an Error
    throw new Error("Authentication token not found. Please log in.");
  }

  // Return the formatted headers object.
  return {
    "Content-Type": "application/json; charset=UTF-8",
    Authorization: `Bearer ${token}`,
  };
};

const getCurrentUserId = async () => {
  try {
    // Assumption: getTokenFromStorage is an async function that reads the value for 'jwt_token'.
    const token = await getTokenFromStorage("jwt_token");

    if (!token) {
      console.log("No JWT token found");
      return null;
    }

    // Assumption: jwtDecode is a synchronous function from a library like 'jwt-decode'.
    let payload;
    try {
      payload = jwtDecode(token);
    } catch (decodeError) {
      console.log("Error decoding JWT token:", decodeError);
      return null;
    }

    // Use optional chaining for safe access, or check existence
    const userId = payload?.id;

    if (!userId) {
      console.log("User ID not found in JWT token");
      return null;
    }

    return userId;
  } catch (error) {
    console.error("Error accessing storage or extracting user ID:", error);
    return null;
  }
};

const getTokenFromStorage = async (key) => {
  // Replace with your actual async storage implementation (e.g., AsyncStorage)
  return localStorage.getItem(key);
};

const decodeToken = (token) => {
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// Consolidated Export
export {
  registerTraveler,
  registerGuide,
  updateGuideFiles,
  registerHotelOwner,
  validateCityZip,
  updateHotelOwnerFiles,
  validateNicDob,
  validateNicGender,
  validateCityDistrict,
  fetchUserById,
  getCurrentUserId,
  decodeToken,
};
